import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateOrderPaymentStatus, updateCODOrderStatus } from '../Actions/orderActions';
import './PaymentGateway.css';
import { useToast } from '../Context/ToastContext';
import { getAuth } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

const PaymentGateway = ({ orderId, paymentMethod, totalAmount, onSuccess, onFailure }) => {
  const [loading, setLoading] = useState(false);
  const { success, error: showError, info } = useToast();
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const dispatch = useDispatch();
  const normalizedMethod = String(paymentMethod || '').toUpperCase();
  const [stage, setStage] = useState('idle'); // idle | initiated
  const userSignin = useSelector((state) => state.userSignin);
  const userInfo = userSignin?.userInfo;

  // Use live key for production mode
  // Fallback to hardcoded live key if env variable is not available
  const RZP_KEY_ID = process.env.REACT_APP_RAZORPAY_KEY_ID || 'rzp_live_RPj3X0NSkrXbXM'; // public
  
  // Payment gateway configuration

  // Endpoint configuration for local development
  const isBrowser = typeof window !== 'undefined';
  const host = isBrowser ? window.location.hostname : '';
  const isLocalhost = /^(localhost|127\.0\.0\.1)$/i.test(host);
  
  // Secure approach: Use proxy function for cross-origin CORS handling
  const EMU_BASE = 'http://localhost:5001/shopie-bazar-e4920/asia-south1';
  const FIREBASE_HOSTING = 'https://shopie-bazar-e4920.web.app';
  
  // Treat BOTH Firebase Hosting default and custom domain as same-origin hosting
  const isHostingDomain = /(shopie-bazar-e4920\.web\.app|shopiebazar\.com|www\.shopiebazar\.com)$/i.test(host);
  
  // Direct function URLs for all requests
  const DIRECT_ORDER_URL = isLocalhost
    ? `${EMU_BASE}/payments_razorpay_order`
    : `https://asia-south1-shopie-bazar-e4920.cloudfunctions.net/payments_razorpay_order`;
    
  const DIRECT_VERIFY_URL = isLocalhost
    ? `${EMU_BASE}/payments_razorpay_verify`
    : `https://asia-south1-shopie-bazar-e4920.cloudfunctions.net/payments_razorpay_verify`;
    
  // Always use direct function URLs to avoid hosting rewrite issues
  const CREATE_URL = isLocalhost
    ? `${EMU_BASE}/payments_razorpay_order`
    : isHostingDomain
      ? '/api/payments/order'  // Same-origin: use Firebase Hosting rewrite
      : DIRECT_ORDER_URL;  // Cross-origin: use direct function URL
    
  const VERIFY_URL = isLocalhost
    ? `${EMU_BASE}/payments_razorpay_verify`
    : isHostingDomain
      ? '/api/payments/verify'  // Same-origin: use Firebase Hosting rewrite
      : DIRECT_VERIFY_URL;  // Cross-origin: use direct function URL
  
  // Payment endpoints configured

  const loadRazorpayScript = () => new Promise((resolve) => {
    if (document.getElementById('razorpay-checkout-js')) return resolve(true);
    const script = document.createElement('script');
    script.id = 'razorpay-checkout-js';
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

  // Common helper to finalize any online payment and update Firestore
  const commitOnlinePayment = (methodLabel, txnId, bankTxnId, amount) => {
    const txnDate = new Date().toLocaleString('en-IN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    }).replace(/(\d{2})\/(\d{2})\/(\d{4}), (\d{2}):(\d{2}):(\d{2})/, '$3-$2-$1 $4:$5:$6.0');

    const paymentData = {
      CustTransaction_No: txnId,
      CustUTR_No: bankTxnId,
      Payment_Method: methodLabel,
      Transaction_Date: txnDate,
      Total_Amount: Number(amount),
      Txn_Discount: 0
    };

    dispatch(updateOrderPaymentStatus(orderId, paymentData));
    setLoading(false);
    onSuccess({
      message: 'Payment completed successfully',
      orderId,
      amount: Number(amount),
      paymentId: txnId,
      gatewayOrderId: bankTxnId,
      paymentMethod: methodLabel,
      paid: true,
    });
  };

  // Paytm Payment Integration (mocked - no auto success)
  const initiatePaytmPayment = async () => {
    setLoading(true);
    try {
      // In a real implementation, you would call your backend to get Paytm token
      // For now, we'll simulate the payment process
      
      const paytmConfig = {
        MID: process.env.REACT_APP_PAYTM_MID || 'YOUR_MERCHANT_ID',
        WEBSITE: process.env.REACT_APP_PAYTM_WEBSITE || 'WEBSTAGING',
        CHANNEL_ID: 'WEB',
        INDUSTRY_TYPE_ID: 'Retail',
        ORDER_ID: orderId,
        CUST_ID: 'CUST_' + Date.now(),
        TXN_AMOUNT: totalAmount.toString(),
        CALLBACK_URL: `${window.location.origin}/payment-callback`,
      };

      // In production, redirect or open SDK here; for now, show waiting screen
      setStage('initiated');
      info('Paytm: waiting for payment confirmation... (test mode)');
      setLoading(false);

    } catch (error) {
      console.error('Payment initiation failed:', error);
      setLoading(false);
      onFailure('Payment initiation failed');
    }
  };

  // Razorpay Payment Integration - Direct Cloud Function URLs (final attempt)
  const initiateRazorpayPayment = async () => {
    setLoading(true);
    try {
      if (!RZP_KEY_ID) {
        setLoading(false);
        return onFailure('Razorpay Key ID missing. Set REACT_APP_RAZORPAY_KEY_ID in .env');
      }

      const ok = await loadRazorpayScript();
      if (!ok) {
        setLoading(false);
        return onFailure('Failed to load Razorpay SDK');
      }

      // DIRECT CLOUD FUNCTION URLS - Last resort approach
      const auth = getAuth();
      const user = auth.currentUser;
      
      // User login check is optional now since we're using public endpoints
      // But we still recommend being logged in for order tracking
      if (!user) {
        console.warn('Proceeding with payment without login - order tracking may be limited');
      }

      // Calculate amount in paise
      const orderAmount = Math.round(Number(totalAmount) * 100);
      
      info('Creating payment order...');
      
      // Step 1: Create Razorpay order via public cloud function endpoint
      const orderResponse = await fetch(CREATE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          orderId: orderId,
          amountInPaise: orderAmount,
          currency: 'INR',
          userId: user ? user.uid : 'guest-user' // Pass user ID in body instead of token
        })
      });

      if (!orderResponse.ok) {
        try {
          const errorData = await orderResponse.json();
          console.error('Order creation failed:', errorData);
          setLoading(false);
          return onFailure(errorData.error || `Payment server error (${orderResponse.status}): ${errorData.message || 'Unknown error'}`);
        } catch (parseError) {
          console.error('Order creation failed with unparseable response:', orderResponse.status, parseError);
          setLoading(false);
          return onFailure(`Payment server error (${orderResponse.status}). Please try again or contact support.`);
        }
      }

      const orderData = await orderResponse.json();
      
      if (!orderData.razorpay_order_id) {
        setLoading(false);
        return onFailure('Invalid order response from server');
      }

      info('Payment order created successfully');
      setStage('initiated');
      
      // Step 2: Open Razorpay with proper order_id
      const options = {
        key: RZP_KEY_ID,
        amount: orderData.amount,
        currency: orderData.currency,
        name: 'Shopie Bazar',
        description: `Order ${orderId}`,
        order_id: orderData.razorpay_order_id, // Real Razorpay order ID from backend
        image: 'https://shopie-bazar-e4920.web.app/logo192.png',
        prefill: {
          name: userInfo?.displayName || userInfo?.fullName || '',
          email: userInfo?.email || '',
          contact: userInfo?.mobile || userInfo?.phone || ''
        },
        theme: { color: '#6b46c1' },
        handler: async function (response) {
          try {
            info('Verifying payment...');
            
            // Step 3: Verify payment via public cloud function endpoint
            const verifyResponse = await fetch(VERIFY_URL, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'X-Requested-With': 'XMLHttpRequest'
              },
              body: JSON.stringify({
                orderId: orderId,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                userId: user ? user.uid : 'guest-user' // Pass user ID in body instead of token
              })
            });

            if (!verifyResponse.ok) {
              try {
                const errorData = await verifyResponse.json();
                console.error('Payment verification failed:', errorData);
                setLoading(false);
                return onFailure(errorData.error || `Verification failed (${verifyResponse.status}): ${errorData.message || 'Unknown error'}`);
              } catch (parseError) {
                console.error('Payment verification failed with unparseable response:', verifyResponse.status, parseError);
                setLoading(false);
                return onFailure(`Payment verification failed (${verifyResponse.status}). Your payment may still have been processed.`);
              }
            }

            const verifyData = await verifyResponse.json();
            
            if (verifyData.ok) {
              success('Payment verified successfully!');
              // Commit to Firestore with verified payment data
              commitOnlinePayment('RAZORPAY', response.razorpay_payment_id, response.razorpay_order_id, totalAmount);
            } else {
              setLoading(false);
              onFailure('Payment verification failed');
            }
          } catch (e) {
            console.error('Payment verification error:', e);
            setLoading(false);
            onFailure('Payment verification failed. Please contact support.');
          }
        },
        modal: {
          ondismiss: function () {
            setLoading(false);
            setStage('idle');
            info('Payment cancelled by user');
          }
        }
      };
      
      const rzp = new window.Razorpay(options);
      rzp.open();
      setLoading(false);
    } catch (error) {
      console.error('Razorpay initiation error:', error);
      setLoading(false);
      
      // Enhanced error handling with more specific messages
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        onFailure('Network connection error. Please check your internet connection.');
      } else if (error.message.includes('CORS')) {
        onFailure('Security policy blocking request. Please contact support with error: CORS policy violation.');
      } else if (error.message.includes('Failed to fetch')) {
        onFailure('Could not connect to payment server. Please try again later.');
      } else if (error.message.includes('NetworkError')) {
        onFailure('Network error occurred. Please check your connection and try again.');
      } else if (error.message.includes('timeout')) {
        onFailure('Payment server took too long to respond. Please try again.');
      } else {
        // Log detailed error for debugging
        console.error('Detailed payment error:', {
          name: error.name,
          message: error.message,
          stack: error.stack,
          url: CREATE_URL
        });
        onFailure(`Payment initiation failed: ${error.message || 'Unknown error'}. Please try again.`);
      }
    }
  };

  // PhonePe Payment Integration (mocked - no auto success)
  const initiatePhonePePayment = async () => {
    setLoading(true);
    try {
      // In production, redirect to PhonePe payment flow created on backend.
      setStage('initiated');
      info('PhonePe: waiting for payment confirmation... (test mode)');
      setLoading(false);
    } catch (error) {
      console.error('PhonePe initiation failed:', error);
      setLoading(false);
      onFailure('PhonePe initiation failed');
    }
  };

  // COD OTP Verification
  const sendCODOTP = async () => {
    if (!mobileNumber || mobileNumber.length !== 10) {
      showError('Please enter a valid 10-digit mobile number');
      return;
    }

    setLoading(true);
    try {
      // In real implementation, send OTP via SMS service
      // For demo, we'll simulate OTP sending
      setTimeout(() => {
        setOtpSent(true);
        setLoading(false);
        info('OTP sent to your mobile number. Use 123456 for demo.');
      }, 1000);
    } catch (error) {
      console.error('OTP sending failed:', error);
      setLoading(false);
      showError('Failed to send OTP. Please try again.');
    }
  };

  const verifyCODOTP = async () => {
    if (otp !== '123456') { // Demo OTP
      showError('Invalid OTP. Please try again.');
      return;
    }

    setLoading(true);
    try {
      dispatch(updateCODOrderStatus(orderId));
      setLoading(false);
      onSuccess('COD order confirmed successfully');
    } catch (error) {
      console.error('OTP verification failed:', error);
      setLoading(false);
      onFailure('OTP verification failed');
    }
  };

  if (normalizedMethod === 'COD') {
    return (
      <div className="payment-gateway cod-verification">
        <h3>Cash on Delivery - Verification Required</h3>
        
        {!otpSent ? (
          <div className="otp-request">
            <p>Please verify your mobile number to confirm COD order:</p>
            <div className="form-group">
              <label>Mobile Number:</label>
              <input
                type="tel"
                value={mobileNumber}
                onChange={(e) => setMobileNumber(e.target.value)}
                placeholder="Enter 10-digit mobile number"
                maxLength="10"
                className="form-control"
              />
            </div>
            <button
              onClick={sendCODOTP}
              disabled={loading || !mobileNumber}
              className="btn btn-primary"
            >
              {loading ? 'Sending OTP...' : 'Send OTP'}
            </button>
          </div>
        ) : (
          <div className="otp-verification">
            <p>OTP sent to +91-{mobileNumber}</p>
            <div className="form-group">
              <label>Enter OTP:</label>
              <input
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                placeholder="Enter 6-digit OTP"
                maxLength="6"
                className="form-control"
              />
            </div>
            <div className="button-group">
              <button
                onClick={verifyCODOTP}
                disabled={loading || !otp}
                className="btn btn-success"
              >
                {loading ? 'Verifying...' : 'Verify & Confirm Order'}
              </button>
              <button
                onClick={() => {
                  setOtpSent(false);
                  setOtp('');
                }}
                className="btn btn-secondary"
              >
                Resend OTP
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Online Gateways
  if (normalizedMethod === 'PAYTM') {
    return (
      <div className="payment-gateway paytm-payment">
        <h3>Pay with Paytm</h3>
        <div className="payment-details">
          <p><strong>Order ID:</strong> {orderId}</p>
          <p><strong>Amount:</strong> ₹{totalAmount}/-</p>
        </div>
        {stage === 'idle' && (
          <button
            onClick={initiatePaytmPayment}
            disabled={loading}
            className="btn btn-primary paytm-pay-btn"
          >
            {loading ? 'Processing...' : `Pay ₹${totalAmount} with Paytm`}
          </button>
        )}
        {stage === 'initiated' && (
          <div className="gateway-wait">
            <div className="spinner" aria-hidden>⏳</div>
            <p>Waiting for Paytm payment confirmation...</p>
            <p className="muted">Do not refresh or close this window.</p>
          </div>
        )}
        <div className="payment-info">
          <p>✓ Secure payment powered by Paytm</p>
          <p>✓ Multiple payment options available</p>
          <p>✓ Instant confirmation</p>
        </div>
      </div>
    );
  }

  if (normalizedMethod === 'RAZORPAY' || normalizedMethod === 'RAZERPAY') {
    return (
      <div className="payment-gateway razorpay-payment">
        <h3>Pay with Razorpay</h3>
        <div className="payment-details">
          <p><strong>Order ID:</strong> {orderId}</p>
          <p><strong>Amount:</strong> ₹{totalAmount}/-</p>
        </div>
        {stage === 'idle' && (
          <button
            onClick={initiateRazorpayPayment}
            disabled={loading}
            className="btn btn-primary"
          >
            {loading ? 'Processing...' : `Pay ₹${totalAmount} with Razorpay`}
          </button>
        )}
        {stage === 'initiated' && (
          <div className="gateway-wait">
            <div className="spinner" aria-hidden>⏳</div>
            <p>Waiting for Razorpay payment confirmation...</p>
            <p className="muted">Do not refresh or close this window.</p>
          </div>
        )}
        <div className="payment-info">
          <p>✓ Secure payment powered by Razorpay</p>
          <p>✓ Multiple payment options available</p>
          <p>✓ Instant confirmation</p>
        </div>
      </div>
    );
  }

  if (normalizedMethod === 'PHONEPE' || normalizedMethod === 'PHONEPAY') {
    return (
      <div className="payment-gateway phonepe-payment">
        <h3>Pay with PhonePe</h3>
        <div className="payment-details">
          <p><strong>Order ID:</strong> {orderId}</p>
          <p><strong>Amount:</strong> ₹{totalAmount}/-</p>
        </div>
        {stage === 'idle' && (
          <button
            onClick={initiatePhonePePayment}
            disabled={loading}
            className="btn btn-primary"
          >
            {loading ? 'Processing...' : `Pay ₹${totalAmount} with PhonePe`}
          </button>
        )}
        {stage === 'initiated' && (
          <div className="gateway-wait">
            <div className="spinner" aria-hidden>⏳</div>
            <p>Waiting for PhonePe payment confirmation...</p>
            <p className="muted">Do not refresh or close this window.</p>
          </div>
        )}
        <div className="payment-info">
          <p>✓ Secure payment powered by PhonePe</p>
          <p>✓ Multiple payment options available</p>
          <p>✓ Instant confirmation</p>
        </div>
      </div>
    );
  }

  // Fallback generic online payment
  return (
    <div className="payment-gateway online-payment">
      <h3>Complete Payment</h3>
      <div className="payment-details">
        <p><strong>Order ID:</strong> {orderId}</p>
        <p><strong>Amount:</strong> ₹{totalAmount}/-</p>
        <p><strong>Method:</strong> {paymentMethod}</p>
      </div>
      {stage === 'idle' && (
        <button
          onClick={initiatePaytmPayment}
          disabled={loading}
          className="btn btn-primary"
        >
          {loading ? 'Processing...' : 'Pay Now'}
        </button>
      )}
      {stage === 'initiated' && (
        <div className="gateway-wait">
          <div className="spinner" aria-hidden>⏳</div>
          <p>Waiting for payment confirmation...</p>
          <p className="muted">Do not refresh or close this window.</p>
        </div>
      )}
    </div>
  );
};

export default PaymentGateway;
